import React, { FC, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/common/Button';
import { CSLoader } from 'src/components/common/CsLoader';
import { Typography } from 'src/components/common/Typography';
import { icons } from 'src/configs/icons';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { CmsPage } from 'src/types/cms/cmsCommonTypes';
import { CmsPromotionPageFields } from 'src/types/cms/cmsPageTypes';
import './styles.scss';

type Props = {
  handleBack: () => void;
  promotionPageInfo: CmsPage<CmsPromotionPageFields>;
  handleRedirect: () => void;
};

const PromotionInfoPage: FC<Props> = ({ handleBack, handleRedirect, promotionPageInfo }) => {
  const { t }: Translation = useTranslation();
  const [iframeHeight, setIframeHeight] = useState<any>(0);
  const [isLoading, setIsLoading] = useState(true);
  const sessionRecorder = useSessionRecorder();

  const banner = useMemo(() => {
    if (isMobile && !!promotionPageInfo?.content?.pageFields.bannerMobile) {
      return promotionPageInfo.content.pageFields.bannerMobile;
    }

    if (!isMobile && !!promotionPageInfo?.content?.pageFields.bannerDesktop) {
      return promotionPageInfo.content.pageFields.bannerDesktop;
    }

    return null;
  }, [promotionPageInfo]);

  const handleIframeLoad = (): void => {
    setIsLoading(false);
  };

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (event.data.type === 'iframeHeight') {
        setIframeHeight(event.data.height);
      }
    });
  }, []);

  useEffect(() => {
    if (promotionPageInfo?.content?.pageFields?.cardTitle) {
      sessionRecorder?.trackEvent('Promotion page view', {
        promotionName: promotionPageInfo.content.pageFields.cardTitle,
      });
    }
  }, [promotionPageInfo]);

  return (
    <div className="promotions-info-page">
      <HelmetProvider>
        <Helmet>
          {promotionPageInfo?.content?.meta?.pageTitle && <title>{promotionPageInfo.content.meta.pageTitle}</title>}

          {promotionPageInfo?.content?.meta?.pageDescription && (
            <meta name="description" content={promotionPageInfo.content.meta.pageDescription} />
          )}

          {promotionPageInfo?.content?.meta?.pageKeywords && (
            <meta name="keywords" content={promotionPageInfo.content.meta.pageKeywords} />
          )}
        </Helmet>
      </HelmetProvider>
      <Button
        onClick={handleBack}
        className="promotions-info-page__back-button-wrapper"
        icon={icons.controlPrev}
        justifyContentValue="flex-start"
      >
        <span>{t('backToPromotion')}</span>
      </Button>
      <div className="promotions-info-page__description-wrapper">
        {!!banner && !promotionPageInfo?.content?.pageFields?.externalContentUrl && (
          <img className="promotions-info-page__description-img" src={banner.path} alt={t('descriptionImage')} />
        )}
        <div className="promotions-info-page__description-title-text-button-wrapper">
          {promotionPageInfo?.content?.pageFields?.externalContentUrl ? (
            <>
              {isLoading && (
                <div className="loader-container">
                  <CSLoader speed="slow" />
                </div>
              )}
              <iframe
                onLoad={handleIframeLoad} // Handle iframe load
                style={{ height: iframeHeight || 'initial', display: isLoading ? 'none' : 'block' }} // Hide iframe while loading
                src={promotionPageInfo?.content?.pageFields?.externalContentUrl}
                className="promo_frame"
              ></iframe>
            </>
          ) : (
            <>
              <Typography
                variant={'h6'}
                children={
                  <span
                    className="promotions-info-page__description-text"
                    dangerouslySetInnerHTML={{ __html: promotionPageInfo?.content?.pageFields.title || '' }}
                  />
                }
              />
              <Typography
                variant={'h6'}
                children={
                  <span
                    className="promotions-info-page__description-text"
                    dangerouslySetInnerHTML={{ __html: promotionPageInfo?.content?.pageFields.description || '' }}
                  />
                }
              />
            </>
          )}
          {promotionPageInfo?.content?.pageFields?.cta &&
            promotionPageInfo?.content?.pageFields?.cta?.variant !== 'noAction' && (
              <div className="promo-btn">
                <Button
                  onClick={handleRedirect}
                  key={promotionPageInfo?.content?.pageFields?.cta?.variant}
                  className="promotions__button promotions__button--description success-filled-btn"
                >
                  {t(promotionPageInfo?.content?.pageFields?.cta?.label)}
                </Button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default PromotionInfoPage;
